import {
  MousePointer2, MousePointer, Move, Hand, ZoomIn,
  FileText, FilePlus, Layers, Ruler, Columns2, AlignJustify,
  Type, TextCursorInput, Link2, WrapText, AlignVerticalSpaceAround, ListOrdered, Hash,
  Image, ImagePlus, Crop, Maximize2, Scissors, Captions,
  Square, Circle, Slash, PenTool, Pencil, Brush, Eraser,
  Paintbrush, Palette, Pipette, Sparkles as Spark, Palette as PaletteIcon,
  AlignHorizontalDistributeCenter, AlignStartHorizontal, ArrowUp, ArrowDown, Lock, Group,
} from "lucide-react";

/**
 * Each tool: { id, label, icon, modes: ['beginner','pro'] | ['pro'] }
 * `modes` controls which mode shows the tool. Beginner = subset, Pro = all.
 */
export const TOOL_GROUPS = [
  {
    id: "selection",
    label: "Selection",
    tools: [
      { id: "select", label: "Selection Tool", icon: MousePointer2, modes: ["beginner", "pro"] },
      { id: "direct-select", label: "Direct Selection Tool", icon: MousePointer, modes: ["pro"] },
      { id: "move", label: "Move Tool", icon: Move, modes: ["beginner", "pro"] },
      { id: "hand", label: "Hand Tool", icon: Hand, modes: ["pro"] },
      { id: "zoom", label: "Zoom Tool", icon: ZoomIn, modes: ["pro"] },
    ],
  },
  {
    id: "page",
    label: "Page · Layout",
    tools: [
      { id: "page", label: "Page Tool", icon: FileText, modes: ["beginner", "pro"] },
      { id: "add-page", label: "Add Page Tool", icon: FilePlus, modes: ["pro"] },
      { id: "master-page", label: "Master Page Tool", icon: Layers, modes: ["pro"] },
      { id: "margins", label: "Margin and Guides Tool", icon: Ruler, modes: ["pro"] },
      { id: "columns", label: "Column Tool", icon: Columns2, modes: ["pro"] },
      { id: "gap", label: "Gap Tool", icon: AlignJustify, modes: ["pro"] },
    ],
  },
  {
    id: "text",
    label: "Text",
    tools: [
      { id: "type", label: "Type Tool", icon: Type, modes: ["beginner", "pro"] },
      { id: "text-frame", label: "Text Frame Tool", icon: TextCursorInput, modes: ["beginner", "pro"] },
      { id: "link-frames", label: "Link Text Frames Tool", icon: Link2, modes: ["pro"] },
      { id: "text-wrap", label: "Text Wrap Tool", icon: WrapText, modes: ["beginner", "pro"] },
      { id: "footnote", label: "Footnote Tool", icon: AlignVerticalSpaceAround, modes: ["pro"] },
      { id: "toc", label: "Table of Contents Tool", icon: ListOrdered, modes: ["pro"] },
      { id: "page-number", label: "Page Number Tool", icon: Hash, modes: ["pro"] },
    ],
  },
  {
    id: "image",
    label: "Image",
    tools: [
      { id: "image-frame", label: "Image Frame Tool", icon: Image, modes: ["beginner", "pro"] },
      { id: "place-image", label: "Place Image Tool", icon: ImagePlus, modes: ["beginner", "pro"] },
      { id: "crop", label: "Crop Image Tool", icon: Crop, modes: ["pro"] },
      { id: "fit-image", label: "Fit Image to Frame Tool", icon: Maximize2, modes: ["pro"] },
      { id: "remove-bg", label: "Remove Background Tool", icon: Scissors, modes: ["pro"] },
      { id: "caption", label: "Image Caption Tool", icon: Captions, modes: ["pro"] },
    ],
  },
  {
    id: "shape",
    label: "Shape · Draw",
    tools: [
      { id: "rect", label: "Rectangle Tool", icon: Square, modes: ["beginner", "pro"] },
      { id: "ellipse", label: "Ellipse Tool", icon: Circle, modes: ["pro"] },
      { id: "line", label: "Line Tool", icon: Slash, modes: ["pro"] },
      { id: "pen", label: "Pen Tool", icon: PenTool, modes: ["pro"] },
      { id: "pencil", label: "Pencil Tool", icon: Pencil, modes: ["pro"] },
      { id: "brush", label: "Brush Tool", icon: Brush, modes: ["pro"] },
      { id: "eraser", label: "Eraser Tool", icon: Eraser, modes: ["pro"] },
    ],
  },
  {
    id: "color",
    label: "Color · Style",
    tools: [
      { id: "fill", label: "Fill Tool", icon: Paintbrush, modes: ["pro"] },
      { id: "stroke", label: "Stroke Tool", icon: PaletteIcon, modes: ["pro"] },
      { id: "eyedropper", label: "Eyedropper Tool", icon: Pipette, modes: ["pro"] },
      { id: "gradient", label: "Gradient Tool", icon: Spark, modes: ["pro"] },
      { id: "swatches", label: "Swatches Tool", icon: Palette, modes: ["pro"] },
    ],
  },
  {
    id: "arrange",
    label: "Arrange · Object",
    tools: [
      { id: "align", label: "Align Tool", icon: AlignStartHorizontal, modes: ["pro"] },
      { id: "distribute", label: "Distribute Tool", icon: AlignHorizontalDistributeCenter, modes: ["pro"] },
      { id: "bring-forward", label: "Bring Forward Tool", icon: ArrowUp, modes: ["pro"] },
      { id: "send-backward", label: "Send Backward Tool", icon: ArrowDown, modes: ["pro"] },
      { id: "lock", label: "Lock Tool", icon: Lock, modes: ["pro"] },
      { id: "group", label: "Group Tool", icon: Group, modes: ["pro"] },
    ],
  },
];

export const TOP_MENU = [
  "File", "Edit", "Layout", "Type", "Object", "Table", "View", "AI", "Export", "Help",
];

/**
 * Map a selected tool to which contextual toolbar variant to render.
 */
export function toolbarFamily(toolId) {
  if (!toolId) return "selection";
  if (["select", "direct-select", "move", "hand", "zoom"].includes(toolId)) return "selection";
  if (["type"].includes(toolId)) return "text";
  if (["text-frame", "link-frames", "text-wrap", "footnote", "toc", "page-number"].includes(toolId)) return "textframe";
  if (["image-frame", "place-image", "crop", "fit-image", "remove-bg", "caption"].includes(toolId)) return "image";
  if (["rect", "ellipse", "line", "pen", "pencil", "brush", "eraser"].includes(toolId)) return "shape";
  if (["page", "add-page", "master-page", "margins", "columns", "gap"].includes(toolId)) return "page";
  if (["ai-generate", "ai-rewrite", "ai-image"].includes(toolId)) return "ai";
  if (["export", "kdp-check", "a11y"].includes(toolId)) return "export";
  return "selection";
}

export const TRIM_SIZES = [
  { id: "6x9", label: "6 × 9 in", width: 6, height: 9 },
  { id: "5.5x8.5", label: "5.5 × 8.5 in", width: 5.5, height: 8.5 },
  { id: "8.5x11", label: "8.5 × 11 in", width: 8.5, height: 11 },
  { id: "7x10", label: "7 × 10 in", width: 7, height: 10 },
  { id: "a4", label: "A4", width: 8.27, height: 11.69 },
  { id: "custom", label: "Custom", width: 6, height: 9 },
];

export const DOC_PRESETS = [
  { id: "recent", label: "Recent" },
  { id: "saved", label: "Saved" },
  { id: "print", label: "Print" },
  { id: "ebook", label: "Ebook" },
  { id: "workbook", label: "Workbook" },
  { id: "ai-start", label: "AI Start" },
  { id: "upload", label: "Upload" },
];
